<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');

const title = ref('');
const isCreating = ref(false);

onMounted(() => {
  store.dispatch('inboxes/get');
});

const whatsappInbox = async () => {
  const ready = inboxes.value?.[0];
  if (ready) return ready;

  const response = await InboxesAPI.get(false);
  return (response.data?.payload || []).find(
    item => item.channel_type === INBOX_TYPES.WHATSAPP
  );
};

const createCampaign = async () => {
  const name = title.value.trim();
  if (!name) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.ERROR'));
    return;
  }

  isCreating.value = true;
  try {
    const inbox = await whatsappInbox();
    if (!inbox) {
      useAlert(t('CAMPAIGN.WHATSAPP.CREATE.NO_INBOX'));
      return;
    }

    const campaign = await store.dispatch('campaigns/create', {
      title: name,
      message: '',
      inbox_id: inbox.id,
      template_params: {
        mode: 'new',
        category: 'MARKETING',
        language: 'es',
        header_type: 'none',
      },
    });
    emit('close');
    router.push({
      name: 'campaigns_whatsapp_show',
      params: {
        accountId: route.params.accountId,
        campaignId: campaign.id,
      },
    });
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE'));
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div
    class="w-[25rem] z-50 min-w-0 absolute top-10 ltr:right-0 rtl:left-0 bg-n-alpha-3 backdrop-blur-[100px] rounded-xl border border-n-weak shadow-md"
  >
    <form class="flex flex-col gap-4 p-6" @submit.prevent="createCampaign">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.TITLE') }}
      </h3>
      <Input
        v-model="title"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
        autofocus
      />
      <div class="flex gap-3">
        <Button
          variant="faded"
          color="slate"
          type="button"
          class="w-full"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
          @click="emit('close')"
        />
        <Button
          type="submit"
          class="w-full"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.CONTINUE')"
          :is-loading="isCreating"
          :disabled="isCreating"
        />
      </div>
    </form>
  </div>
</template>
